import dynamic from 'next/dynamic';
import { useTranslation, i18n } from 'next-i18next';
import { useSearchParams } from 'next/navigation';
import { parseCookies } from 'nookies';
import { useRouter } from 'next/router';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { lastValueFrom } from 'rxjs';

import { useAppDispatch, useAppSelector } from '@/redux';
import {
  hidePageProgress, hideProductModal,
  showLoginModal, showPageProgress, showProductModal,
} from '@/redux/slices/gui.slice';

import {
  setCategInfos,
  getCategWithFiltersAndProductsSuccess,
  updateCategFiltersSuccess,
  getCategWithFiltersAndProductsError,
  updateCategFiltersError, selectCateg, selectCategEspots, applyFilters,
} from '@/redux/slices/categ.slice';

import { NS } from '@/constants';
import { PAGE_SIZE, FILTERS } from '@/constants/categ';
import { ESPOTS } from '@/constants/espot';
import { FEATURES } from '@/constants/features';
import { PAGES } from '@/constants/pages';
import { COOKIES } from '@/constants/cookies';
import { ATTRIBUTES } from '@/constants/product';
import { usePrimeurUser } from '@/hooks/usePrimeurUser';
import { useServices } from '@/services/useServices';
import {
  isObjectEmpty,
  isUserEmpty,
} from '@/utils/comparisonUtils';
import { completePromises } from '@/utils/completePromises';
import { getAttributeValue, getCurrentProductItem } from '@/utils/productUtils';
import { isFeatureEnabled } from '@/utils/millesimaUtils';

import { scrollToPageTop } from '@/utils/scroll';

import {
  getSelectedFiltersInfos,
  deleteFilterString,
  parseFilters,
  isNote
} from '@/utils/categUtils';
import BreadcrumbContainer from '@/components/common/BreadcrumbContainer';
import MetaTagsMillesima from '@/components/layout/MetaTagsMillesima';
import { useScreenSizeContext } from '@/components/ScreenSize.context';

const RestrictedArea = dynamic(() => import('@/components/common/RestrictedArea'));
RestrictedArea.displayName = 'RestrictedArea';
const TagList = dynamic(() => import('@/components/common/TagList'));
TagList.displayName = 'TagList';
const Tag = dynamic(() => import('@/components/common/Tag'));
Tag.displayName = 'Tag';
const SortDesktop = dynamic(() => import('@/components/categ/SortDesktop'));
SortDesktop.displayName = 'SortDesktop';
const CallToActionContainer = dynamic(() => import('@/components/common/callToAction/CallToActionContainer'));
CallToActionContainer.displayName = 'CallToActionContainer';
const FiltersLayout = dynamic(() => import('@/components/categ/filters/FiltersLayout'));
FiltersLayout.displayName = 'FiltersLayout';
const TopCategory = dynamic(() => import('@/components/categ/TopCategory'));
TopCategory.displayName = 'TopCategory';
const NoSearchResult = dynamic(() => import('@/components/search/NoSearchResult'));
NoSearchResult.displayName = 'NoSearchResult';
const QuickMenu = dynamic(() => import('@/components/categ/QuickMenu'));
QuickMenu.displayName = 'QuickMenu';
const DynamicLayout = dynamic(() => import('@/components/layout/DynamicLayout'));
DynamicLayout.displayName = 'DynamicLayout';
const TopBannerEspot = dynamic(() => import('@/components/layout/espots/TopBannerEspot'));
TopBannerEspot.displayName = 'TopBannerEspot';
const DescriptionCard = dynamic(() => import('@/components/categ/DescriptionCard'));
DescriptionCard.displayName = 'DescriptionCard';
const ModalProduct = dynamic(() => import('@/components/categ/ModalProduct'), { ssr: false });
ModalProduct.displayName = 'ModalProduct';
const ModalAlertPrimeurInfos = dynamic(() => import('@/components/primeurAlert/ModalAlertPrimeurInfos'));
ModalAlertPrimeurInfos.displayName = 'ModalAlertPrimeurInfos';
const CategCard = dynamic(() => import('@/components/categ/CategCard'));
CategCard.displayName = 'CategCard';
const Pagination = dynamic(() => import('@/components/categ/Pagination'), { ssr: false });
Pagination.displayName = 'Pagination';
const CategFiltersMobile = dynamic(() => import('@/components/categ/winebox/CategFilters'));
CategFiltersMobile.displayName = 'CategFiltersMobile';
const DisplayNbProducts = dynamic(() => import('@/components/categ/DisplayNbProducts'));
DisplayNbProducts.displayName = 'DisplayNbProducts';
const Switch = dynamic(() => import('@/components/common/design-system/Switch.ds'));
Switch.displayName = 'Switch';

import styles from './CategView.module.scss';
import { URLS } from '@/constants/urls';
import { Toggle } from '@/components/common/design-system/Toggle.ds';
import ProductListing from '@/components/categ/ProductListing';

const NB_SCROLLTO_DESC_RECURSION_MAX = 3;
const defaultPage = 1;
const defaultSort = 1;

export const normalize = (value, def) => value ? +value : def;

export const CategView = (props) => {
  const {
    filterQuerySSR, pageSSR, sortSSR, categId, isProtectedCateg,
    originalUrl, searchMode, searchTerm, pageSize, productsForEc,
    hasFoundProductInSearchResult, noSearchResultProductsCarousel, noSearchResultEspot,
    ratingValue, ratingCount, hashUserFromQuery, topBannerClosed,
    backEvent, resetBackEvent
  } = props;

  const dispatch = useAppDispatch();
  const { isMobileDevice, alertPrimeurInfosModalVisible } = useAppSelector(({ gui }) => gui);

  const loggedUser = useAppSelector(({ auth }) => auth.loggedUser);
  const storeConf = useAppSelector(({ root }) => root.storeConf);
  const isEc = isUserEmpty(loggedUser) ? false : loggedUser.isEc;

  const { locale, currencyISO: currency, url } = storeConf;

  const { isMobile: isMobileView, isDesktop } = useScreenSizeContext();
  const categ = useAppSelector(selectCateg);
  const espot = useAppSelector(selectCategEspots);

  const { datalayerService, productsService } = useServices();
  const { t } = useTranslation([NS.COMMON, NS.CATEG, NS.ALTER]);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const topListingRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const query = useSearchParams();
  const clientSideFilterQuery = query.get('filter') || '';
  const products = categ.products;
  const nbTotalRows = categ.nbTotalRows;
  const topCategory = categ.top;
  const filtersState = useMemo(() => (
    parseFilters(categ.filters, clientSideFilterQuery, storeConf.storeId)
  ), [categ.filters]);
  const [pageState, setPage] = useState(pageSSR);
  // const sortState = query.get('sort') || '';
  const clientSideSortQuery = parseInt(query.get('sort')) || defaultSort;

  const [sortState, setSort] = useState(clientSideSortQuery);
  const [onlyCUChecked, setOnlyCUChecked] = useState(false);
  const [isSwitchDisable, setIsSwitchDisable] = useState(false);
  const selectedFiltersInfos = useAppSelector(({ categ }) => categ.currentlyAppliedFilters);
  const [topBannerAlreadyClosed, setTopBannerAlreadyClosed] = useState(topBannerClosed);

  if (clientSideFilterQuery && clientSideFilterQuery?.includes(FILTERS.ONLY_CU) && !onlyCUChecked) {
    setOnlyCUChecked(true);
  }

  if (!clientSideFilterQuery?.includes(FILTERS.ONLY_CU) && onlyCUChecked) {
    setOnlyCUChecked(false);
  }

  if (clientSideFilterQuery && clientSideFilterQuery?.includes(FILTERS.ONLY_CU) && !onlyCUChecked) {
    setOnlyCUChecked(true);
  }

  if (!clientSideFilterQuery?.includes(FILTERS.ONLY_CU) && onlyCUChecked) {
    setOnlyCUChecked(false);
  }

  if (sortState !== clientSideSortQuery) {
    setSort(clientSideSortQuery);
  }

  useEffect(() => {
    if (isMobileView) {
      goDefaultLayout();
    }
  }, [isMobileView]);

  useEffect(() => {
    scrollToPageTop();
  }, [pageState]);

  useEffect(() => {
    const syncViewWithFilters = async () => {
      if (!filterQuerySSR) {
        await refreshView({ page: pageState, sort: sortState, filterQuery: query.get('filter') || '' });
      }
      resetBackEvent();
    };

    if (backEvent) {
      syncViewWithFilters();
    }
  }, [query?.get('filter')]);

  usePrimeurUser({ hashUserFromQuery });

  useEffect(() => {
    const isClient = typeof window !== 'undefined';
    const search = isClient ? window.location.search : '';
    setIsSwitchDisable(clientSideFilterQuery?.includes(FILTERS.FORMAT) || search.includes(FILTERS.FORMAT));
  }, [filtersState, clientSideFilterQuery]);

  useEffect(() => {
    // After connection with an Exclusive Cellar account, we must get EC products
    if (isEc && !productsForEc) {
      refreshView({ page: pageState, sort: sortState, filterQuery: clientSideFilterQuery });
    }
  }, [isEc]);

  useEffect(() => {
    if (categ?.isPrivate && !loggedUser) {
      dispatch(showLoginModal());
    }
  }, [categ, loggedUser]);

  useEffect(() => {
    if (categ && typeof window !== 'undefined') {
      updateDatalayer();
    }
  }, []);

  useEffect(() => {
    if (!window.location.hash) {
      const bodyRect = document.body?.getBoundingClientRect();
      const topListingRect = topListingRef.current?.getBoundingClientRect() as DOMRect;
      const offsetTop = topListingRect?.top - bodyRect?.top;

      if (selectedFiltersInfos?.length > 0) {
        scrollToPageTop(offsetTop, 'smooth');
      }
    }
  }, [selectedFiltersInfos, pageState]);

  useEffect(() => {
    return () => {
      dispatch(hidePageProgress());
      dispatch(hideProductModal());
    };
  }, []);

  const updateDatalayer = () => {
    datalayerService.updateCategDatalayer(
      products, clientSideFilterQuery, sortState, categ?.sortOrder,
      categ, searchMode, storeConf.storeId, categ?.nbTotalRows, searchTerm
    );
  };

  /**
   * Refresh products list according to the current filters and parameters
   */
  const refreshView = async ({ page, sort, filterQuery, searchMode, searchTerm }: any) => {
    dispatch(showPageProgress());
    page = page ?? (pageState || defaultPage);
    sort = sort ?? (sortState || defaultSort);
    filterQuery = filterQuery ?? (clientSideFilterQuery || '');
    searchMode = searchMode ?? (props.searchMode || false);
    searchTerm = searchTerm ?? (props.searchTerm || '');

    const queryParams = [
      page !== defaultPage && `page=${page}`,
      sort !== defaultSort && `sort=${sort}`,
      filterQuery !== '' && `filter=${filterQuery}`
    ].filter((p) => !!p).join('&');

    let url = searchMode ? `/search?searchTerm=${searchTerm}` : (categ?.isSpirit ? `${PAGES.SPIRIT}/${router.query.spiritSlug}` : `/${router.query.slug}`);
    if (queryParams) {
      url += `${url.includes('?') ? '&' : '?'}${queryParams}`;
    }

    // Shallow routing just changes the URL, but the page don't reload itself
    await router.push(router.pathname, url, { shallow: true });

    if (searchTerm) {
      productsService.getSearchProducts({
        storeId: storeConf.storeId,
        searchTerm,
        page,
        size: PAGE_SIZE.LIST,
        filter: filterQuery,
        sort,
        ec: !!loggedUser?.isEc
      }).then((result: any) => {
        dispatch(hidePageProgress());
        dispatch(updateCategFiltersSuccess({
          content: {
            ...result.categ,
            filters: result.filters ? parseFilters(result.filters, filterQuery, storeConf.storeId) : []
          }
        }));
      }).catch((error)=> {
        dispatch(hidePageProgress());
        dispatch(updateCategFiltersError({ error }));
      });
    } else {
      productsService.getCategWithFiltersAndProducts({
        storeId: storeConf.storeId,
        categ: categId,
        page,
        size: PAGE_SIZE.LIST,
        filter: filterQuery,
        sort,
        ec: !!loggedUser?.isEc
      }).then((result: any) => {
        setPage(page);
        dispatch(applyFilters(
          getSelectedFiltersInfos(t, filterQuery, parseFilters(result.filters, filterQuery, storeConf.storeId), locale, currency)
        ));
        dispatch(hidePageProgress());
        dispatch(getCategWithFiltersAndProductsSuccess({
          content: {
            ...result.categ,
            top: result.top,
            filters: parseFilters(result.filters, filterQuery, storeConf.storeId),
            products: result.products,
            nbTotalRows: result?.nbTotalRows,
            sortOrder: result?.sortOrder
          }
        }));
      }).catch((error) => {
        dispatch(hidePageProgress());
        dispatch(getCategWithFiltersAndProductsError({ error }));
      });
    }
  };

  /**
   * Called when a filter (price, bloc, checkbox or critic) is set/unset in the filters component
   * @param {object} changes
   *  {string} nameId The filter nameId
   *  {string} valueId The filter valueId
   *  {boolean} check True to add/update, false to remove
   *  {boolean} price True if price filter
   *  {number} page The page number
   *  {string} structure The view mode
   *  {string} sort The sort mode
   *  {number} minPrice The min price criteria
   *  {number} maxPrice The max price criteria
   */
  const onFilterChange = (changes: any = {}) => {
    let filterQuery = changes.filterQuery ?? clientSideFilterQuery;
    const page = +(changes.nameId ? defaultPage : changes.page ?? pageState ?? defaultPage);
    const sort = +(changes.sort ?? (sortState || defaultSort));
    if (changes.nameId) {
      const { nameId, valueId, check, price, minPrice, maxPrice } = changes;
      if (price || nameId === 'Price') {
        filterQuery = updateFilterString(filterQuery, 'minPrice', minPrice, !!minPrice || minPrice === 0);
        filterQuery = updateFilterString(filterQuery, 'maxPrice', maxPrice, !!maxPrice);
      } else if (isNote(nameId)) {
        filterQuery = updateFilterString(filterQuery, nameId, valueId, false);// We delete old selected critic filter
        filterQuery = updateFilterString(filterQuery, nameId, valueId, true); // And we replace it by new critic filter
      } else {
        filterQuery = updateFilterString(filterQuery, nameId, valueId, check);
      }
    }

    refreshView({ page, sort, filterQuery });
  };

  /**
   * Update, add or remove filter from filter string
   * @param {string} filterString The source filter string
   * @param {string} nameId The filter name (ex: millesime, couleur)
   * @param {string} valueId The filter valueId (ex: 29 for millesime, 5 for couleur)
   * @param {boolean} check True if filter must be set, false to remove it
   * @return {string} the new filter string
   */
  const updateFilterString = (filterString, nameId, valueId, check) => {
    // Build a key/value object from the given filters string
    // Before: "filter1:v1|filter2:v2|filter3:v3|note_rp:88|note_rp:88+|note_rp:89"
    // After: { filter1: v1, filter2: v2, filter3: v3, note_rp: [note_rp:88, note_rp:88+, note_rp:89]}
    let filters = filterString.split('|').reduce((obj, item) => {
      const [key, value] = item.split(':');
      // Verify if there is already a filter for critics
      if (isNote(filterString) && isNote(key)) {
        // Need special case if the first iteration
        if (isObjectEmpty(obj)) {
          return { [key]: [`${key}:${value}`] };
        } else {
          // Create array with structure : [note_rp:88, note_rp:88+, note_rp:89]
          return { ...obj, [key]: Array.isArray(obj[key]) ? [...obj[key], `${key}:${value}`] : [obj[key], `${key}:${value}`] };
        }
      } else {
        return { ...obj, [key]: value };
      }
    }, {});

    // Add or overwrite the requested filter entry
    filters[nameId] = check ? (
      isNote(nameId) ? valueId.join('|' + nameId + ':') : valueId
    ) : undefined;

    if (isNote(nameId) && !check) {
      filters = Object.fromEntries(Object.entries(filters).filter(([key]) => !isNote(key)));
    }

    // Rebuild the filters string
    filters = Object.keys(filters).filter((k) => (!!filters[k] || filters[k] === 0)).reduce((arr, k) => [
      ...arr,
      isNote(k) && Array.isArray(filters[k]) ? filters[k].join('|') : `${k}:${filters[k]}` // Need join notes Tab before join all filters
    ], []).join('|');

    return filters.replace('||', '|');
  };

  const onSortChange = (value) => {
    onFilterChange({ page: defaultPage, sort: value });
  };

  const onPageChange = (page) => {
    onFilterChange({ page });
  };

  const deleteFilter = (tag) => {
    if (isNote(tag.nameId)) {
      onFilterChange({ filterQuery: deleteFilterString(clientSideFilterQuery, tag) });
    } else {
      onFilterChange({ ...tag, check: false });
      if (tag.nameId === ATTRIBUTES.CONDITIONING && tag.valueId === FILTERS.ONLY_CU) {
        setOnlyCUChecked(!onlyCUChecked);
      }
    }
  };

  const deleteAll = () => {
    onFilterChange({ filterQuery: '' });
    setOnlyCUChecked(false);
  };

  /**
   * Scroll to descriptionRef
   * Call this function recursively if the "scrollTo" doesn't work (or the position)
   * We can adjust the position to scroll to a position with an offset
   * The number of recursive call is limited by a constant "NB_SCROLLTO_DESC_RECURSION_MAX"
   *
   * @param {number} offset Offset to apply to the descriptionRef position
   * @param {*} iterationIndex Current index of the number of call to seeDescription recursively (for limit that number)
   * @return {void}
   */
  const seeDescription = (offset = 0, iterationIndex = 0) => {
    if (!descriptionRef?.current?.offsetTop) {
      return console.error('no reference is passed');
    }
    window.scrollTo(0, descriptionRef?.current?.offsetTop + offset);
    if (isDesktop) {
      setTimeout((itIndex) => {
        const getTopOfElement = descriptionRef?.current?.getBoundingClientRect().top as number;
        const eltTop = Math.round(getTopOfElement);
        if (eltTop !== 0 && eltTop !== descriptionRef?.current?.offsetTop &&
          ((window.innerHeight + window.scrollY) < document.body.offsetHeight) &&
          (itIndex < NB_SCROLLTO_DESC_RECURSION_MAX)) {
          seeDescription(eltTop, itIndex + 1);
        }
      }, 100, iterationIndex);
    }
  };

  const showCUProducts = () => {
    const changes = { nameId: ATTRIBUTES.CONDITIONING, valueId: FILTERS.ONLY_CU };
    onFilterChange(!onlyCUChecked ?
      { ...changes, check: true } :
      { ...changes, check: false }
    );
    setOnlyCUChecked((previous) => !previous);
  };

  const goDefaultLayout = async () => {
    const queryParams = [
      pageState !== 1 && `page=${pageState}`,
      sortState !== 1 && `sort=${sortState}`,
      clientSideFilterQuery !== '' && `filter=${clientSideFilterQuery}`
    ].filter((p) => !!p).join('&');

    let url: string = `/${router.query.slug}`;

    if (queryParams) {
      url += `${url.includes('?') ? '&' : '?'}${queryParams}`;
    }

    await router.push(url, url);
  };

  const onProductSelection = (product) => {
    // setSelectedProduct(product);
    if (product) {
      datalayerService.setQuickViewProduct([{
        productName: product.productName,
        SKU: product.partnumber,
        brand: getAttributeValue(product, 'producteur', ''),
        country: getAttributeValue(product, 'pays', ''),
        region: getAttributeValue(product, 'region', ''),
        appellation: getAttributeValue(product, 'appellation', '')
      }]);
      // dispatch(showProductModal());
    }
  };

  return (
    <>
      <MetaTagsMillesima
        categ={categ}
        nbTotalRows={nbTotalRows}
        page={pageState}
        pageSize={pageSize}
        searchMode={searchMode}
        originalUrl={originalUrl}
        isMobileDevice={isMobileDevice}
        isSpirit={categ?.isSpirit}
      />
      {(isProtectedCateg && !isEc) ? (
        <div className={styles['restricted-area-spacer']}>
          <RestrictedArea
            showButton={!loggedUser}
            buttonText={t('account.login')}
            onButtonClick={() => dispatch(showLoginModal())}
          />
        </div>
      ) : (!searchMode || hasFoundProductInSearchResult) ? (
        <>
          <div data-test-id="categPage" className={`container-fluid p-0 ${styles['categ-page']}`}>
            {alertPrimeurInfosModalVisible && (
              <ModalAlertPrimeurInfos isOpen={alertPrimeurInfosModalVisible} />
            )}

            {isDesktop && categ?.espotTopBanner && !topBannerAlreadyClosed && (
              <TopBannerEspot
                espot={categ?.espotTopBanner}
                topBannerAlreadyClosed={topBannerAlreadyClosed}
                setTopBannerAlreadyClosed={setTopBannerAlreadyClosed}
                contentClassname={`container ${styles['container-width']}`}
              />
            )}

            <div className={`row m-0 justify-content-between ${categ?.htmlDepartementPage ? styles['listing-header'] : ''}`}>
              {categ?.htmlDepartementPage && (
                <div
                  data-test-id="htmlDepartementPage"
                  className="w-100"
                  dangerouslySetInnerHTML={{ __html: categ?.htmlDepartementPage }}
                />
              )}
              {isDesktop && (
                <div className={`container ${styles['container-width']}`}>
                  <BreadcrumbContainer
                    homeLabel={categ?.isSpirit ? t('breadcrumbHomeLabelSpirit') : t('breadcrumbHomeLabel')}
                    {...categ}
                    baseUrl={url}
                  />
                </div>
              )}
            </div>

            {categ && (
              <>
                <div className={`container ${styles['container-width']}`}>
                  <CategCard categ={categ} isMobile={!isDesktop} seeDescription={seeDescription} />
                </div>

                {categ?.headerEspots?.length > 0 && (
                  <div className={`container ${styles['container-width']} mil-mt-2`}>
                    <DynamicLayout
                      componentList={categ?.headerEspots}
                      isMobileDevice={isMobileDevice}
                    />
                  </div>
                )}
              </>
            )}
            <div className={`container p-0 ${styles['container-width']}`}>
              <div className={styles['content-layout']}>
                {!isMobileView ? (
                  <div className={styles['top-listing-container']} ref={topListingRef}>
                    <div className={styles['total-products']} ref={topListingRef}>
                      <span>{t('category:results.total_plural', { count: nbTotalRows })}</span>
                      <Toggle
                        iconRight={{
                          src: URLS.LIST_VIEW_FILLED, width: 16, height: 16, alt: 'change layout',
                        }}
                        iconLeft={{
                          src: URLS.GRID_VIEW_OUTLINED, width: 16, height: 16, alt: 'current layout', onClick: goDefaultLayout,
                        }}
                      />
                    </div>
                    <div className={styles['switch-and-sort']}>
                      <Switch
                        defaultState={onlyCUChecked}
                        onClick={showCUProducts}
                        disabled={isSwitchDisable}
                        paragraph={t(`filter.${FILTERS.ONLY_CU}`, { ns: NS.CATEG })}
                      />
                      <SortDesktop
                        t={t} selectedSort={sortState} onSortChange={onSortChange} isProducerCategory={categ.isProducerCategory}
                      />
                    </div>
                    <TagList t={t} onDeleteAll={deleteAll}>
                      {selectedFiltersInfos.map((tag) => (
                        <Tag key={tag.filterString} displayClose clickable onClose={() => deleteFilter(tag)}>
                          {tag.label}
                        </Tag>
                      ))}
                    </TagList>
                  </div>
                ) : (
                  <>
                    <CategFiltersMobile
                      innerModal
                      filters={filtersState}
                      deleteFilter={deleteFilter}
                      deleteAll={deleteAll}
                      onSortChange={onSortChange}
                      onFilterChange={onFilterChange}
                      sort={sortState}
                      selectedFiltersInfos={selectedFiltersInfos}
                      withPadding
                      isProducerCategory={categ.isProducerCategory}
                      showCUProducts={showCUProducts}
                      onlyCUChecked={onlyCUChecked}
                    />
                    <DisplayNbProducts nbTotalRows={nbTotalRows} customClassName={styles['total-products']}/>
                  </>
                )}
                <section className={styles['product-listing']}>
                  <ProductListing
                    unitBottleOnly={onlyCUChecked}
                    products={products}
                    categ={categ}
                    onProductSelection={onProductSelection}
                    url={originalUrl}
                    storeId={storeConf.storeId}
                    sort={sortState}
                    activeFilter={clientSideFilterQuery}
                    searchMode={searchMode}
                    isMobile={isMobileView}
                    ratingCount={ratingCount}
                    ratingValue={ratingValue}
                    storeConf={storeConf}
                    espotListingPush={categ?.espotListingPush}
                    filterQuery={clientSideFilterQuery}
                  />
                  {nbTotalRows > pageSize && (
                    <div className={styles['col-pagination-bottom']}>
                      <Pagination
                        t={t}
                        categ={categ}
                        nbTotalRows={nbTotalRows}
                        page={pageState}
                        pageSize={pageSize}
                        filterQuery={clientSideFilterQuery}
                        onChange={onPageChange}
                        isMobile={!isDesktop}
                      />
                    </div>
                  )}
                  {categ?.categLinks?.length > 0 && (
                    <div className={`row mx-0 ${styles['quick-menu-container']}`}>
                      <div className="col-12">
                        <QuickMenu t={t} categLinks={categ?.categLinks} isMobile={!isDesktop} />
                      </div>
                    </div>
                  )}
                </section>
                {isDesktop ? (
                  <div className={`${styles['category-filters']}`} data-rbf="categ-filters-desktop">
                    <FiltersLayout
                      onChange={onFilterChange}
                      filters={filtersState}
                      selectedFiltersInfos={selectedFiltersInfos}
                    />
                    {topCategory?.length > 0 && (
                      <div className={styles['top-category']}>
                        <TopCategory t={t}
                          topCategory={topCategory}
                          isMobileDevice={false}
                          numberOfLinks={topCategory.length}
                        />
                      </div>
                    )}
                    {/** FIXME: Handle video (categ?.idYoutube) here  */}
                  </div>
                ) : topCategory?.length > 0 && (
                  <div className={styles['top-category-mobile']}>
                    <TopCategory t={t} isMobileDevice={true} topCategory={topCategory} numberOfLinks={topCategory?.length} />
                  </div>
                )}

                {/* {selectedProduct && ( */}
                {/*   <ModalProduct */}
                {/*     t={t} */}
                {/*     product={selectedProduct} */}
                {/*     categ={categ} */}
                {/*     sort={sortState} */}
                {/*     activeFilter={clientSideFilterQuery} */}
                {/*     searchMode={searchMode} */}
                {/*   /> */}
                {/* )} */}

              </div>
            </div>
          </div>

          {categ?.footerEspots?.length > 0 && (
            <div className={`container ${styles['container-width']}`}>
              <DynamicLayout
                componentList={categ?.footerEspots}
                isMobileDevice={isMobileDevice}
                lazy={true}
              />
            </div>
          )}

          {categ?.longDescription && (
            <div className="mt-4" id="categDesc" data-test-id="categDesc" ref={descriptionRef}>
              <DescriptionCard t={t} categ={categ} />
            </div>
          )}

          {categ?.htmlFooter && (
            <div data-test-id="htmlFooter" className="w-100" dangerouslySetInnerHTML={{ __html: categ?.htmlFooter }} />
          )}

          {espot?.content?.length > 0 && (
            <div id="blogPost" className={styles.BlogPost} dangerouslySetInnerHTML={{ __html: espot?.content }} />
          )}

          {!isDesktop && (
            <div className={`col ${styles['breadcrumb-mobile']}`}>
              <BreadcrumbContainer
                homeLabel={categ?.isSpirit ? t('breadcrumbHomeLabelSpirit') : t('breadcrumbHomeLabel')}
                {...categ}
                baseUrl={originalUrl}
              />
            </div>
          )}
        </>
      ) : (
        <NoSearchResult
          t={t}
          topCategory={topCategory}
          products={noSearchResultProductsCarousel?.products}
          espot={noSearchResultEspot}
          searchTerm={searchTerm}
          isMobileView={isMobileView}
        />
      )}
    </>
  );
};

export default CategView;

/**
  * Prefetch function  called on server side
  * @param {object} ctx The server side context
  * @param {object} store The server side application store
  * @param {object} props The seo page props
  * @return {object} Return an object containing the component props
  *    layoutOption : object containing the needed page layout (see _app component)
  *    namespaces : an array containing the needed translation namespaces
  */
export const prefetchView = async (ctx, { getState, dispatch, services }, props) => {
  const { query, resolvedUrl } = ctx;
  const {
    root: { storeConf, baseUrl },
    auth: { loggedUser },
    cart: { content: { storeIdForPrice: storeIdForPriceInCart } },
    gui: { storeIdForPrice: storeIdForPriceInStore }
  } = getState();
  const params = new URLSearchParams(resolvedUrl.split('?')[1]);
  const { productsService, espotService } = services;
  const { hashUser: hashUserFromQuery, searchTerm = '' } = query;
  const isEC = !!loggedUser?.isEc;
  const { storeId } = props;
  const storeIdForPrice = storeIdForPriceInStore || storeIdForPriceInCart;

  const sort = normalize(params.get('sort'), defaultSort);
  const page = normalize(params.get('page'), defaultPage);
  const filterQuery = params.get('filter') || '';
  const searchMode = !!searchTerm;

  let categ;
  let content;
  let categId;
  let products;
  let filters;
  let htmlBlogPost;
  let htmlBlogPostObject;
  let hasFoundProductInSearchResult = true;
  let noSearchResultProductsCarousel;
  let noSearchResultEspot;

  if (props.id) {
    categId = Array.isArray(props.id) ? props.id[0].replace(`-${storeId}`, '') : props.id.replace(`-${storeId}`, '');
  } else if (searchMode) {
    categ = { urlKeyword: `search?searchTerm=${searchTerm}` };
  } else {
    const stateCateg = getState().categ;
    categId = stateCateg.categId;
    categ = stateCateg.content;
  }

  if (categId) {
    let isCategViewWB = false;
    if (categId.includes('caisse-panachee')) {
      isCategViewWB = true;
    }

    [content, htmlBlogPost] = await completePromises([
      productsService.getCategWithFiltersAndProducts({
        storeId, categ: categId, page, size: PAGE_SIZE.LIST, filter: filterQuery, sort, isCategViewWB, ec: isEC
      }, storeIdForPrice),
      productsService.getBlogPostObject(storeConf.site, categId)
    ]);

    if (htmlBlogPost && htmlBlogPost !== '') {
      try {
        htmlBlogPostObject = JSON.parse(htmlBlogPost);
      } catch (err) {
        console.log('Failed to parse htmlBlogPost:', err.message);
        const content = await productsService.getBlogPost(storeConf.site, categId);
        htmlBlogPostObject = { content };
      }
    }

    products = {
      products: content?.products,
      nbTotalRows: content?.nbTotalRows,
      sortOrder: content?.sortOrder
    };

    categ = {
      ...content?.categ,
      top: content?.top,
      filters: content?.filters,
      ogImage: htmlBlogPostObject?.ogImage
    };
  } else if (searchMode) {
    products = await productsService.getSearchProducts({
      storeId, searchTerm, page, size: PAGE_SIZE.LIST, filter: filterQuery, sort, ec: isEC
    }, storeIdForPrice);

    if (products.filters) {
      categ.filters = products.filters;
    }

    if (!products?.products?.length) {
      [noSearchResultProductsCarousel, noSearchResultEspot] = await completePromises([
        productsService.getProductFilterByCateg(
          storeId, 'best-sellers', 1, 5, '', 1, isEC, storeIdForPrice
        ),
        lastValueFrom(espotService.getEspotDataByLocation(storeId, ESPOTS.NO_SEARCH_RESULT))
      ]);
      hasFoundProductInSearchResult = false;
    }
  }

  const categInfo = {
    ...categ,
    ...products,
    filters: parseFilters(!isObjectEmpty(categ?.filters) ? categ.filters : [], filterQuery, storeId),
    page,
    sort,
    searchTerm
  };

  dispatch(setCategInfos({ categId, content: categInfo, espot: htmlBlogPostObject, filterQuery, filters }));

  const filtersState = parseFilters(categInfo.filters, filterQuery, storeConf?.storeId);
  const selectedFiltersInfos = getSelectedFiltersInfos(i18n?.t, filterQuery, filtersState, storeConf?.locale, storeConf?.currency);

  dispatch(applyFilters(selectedFiltersInfos));

  let specialImg;
  if (searchMode && products) {
    specialImg = products.specialImg;
  } else if (categ) {
    specialImg = categ?.specialImg;
  }

  const namespacesRequired = [NS.COMMON, NS.CATEG, NS.CHECKOUT, NS.ALTER];

  if (isFeatureEnabled(storeConf, FEATURES.MODULE_CUSTO)) {
    namespacesRequired.push(NS.CUSTOMIZATION);
  }

  if (loggedUser?.isAdmin) {
    namespacesRequired.push(NS.ADMIN);
  }

  const topBannerClosed = !!parseCookies(ctx)[`${COOKIES.TOP_BANNER_ESPOT.NAME}${categInfo.espotTopBanner?._id}`];

  return {
    layoutOptions: {
      withScrollButton: true
    },
    namespacesRequired,
    originalUrl: baseUrl,
    specialImg,
    pageSize: PAGE_SIZE.LIST,
    searchTerm,
    searchMode,
    isProtectedCateg: !!categ?.isPrivate,
    hashUserFromQuery,
    // To know if we got Exclusive Cellar products
    productsForEc: isEC,
    hasFoundProductInSearchResult,
    noSearchResultProductsCarousel,
    noSearchResultEspot,
    categId: categId,
    filterQuerySSR: filterQuery,
    pageSSR: page,
    sortSSR: sort,
    topBannerClosed
  };
};
